// Collection Manager route. Owner-only view of their cards, portfolio metrics,
// and a valuation series that only renders points where real valuations exist.

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Plus, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { formatGBP } from "@/lib/format";
import { useTranslation } from "@/i18n";
import { formatGradingBadge } from "@/lib/grading/companies";
import {
  listCollections,
  createCollection,
  listCollectionItems,
  addCollectionItem,
  archiveCollectionItem,
  getCollectionSummary,
} from "@/lib/collection.functions";

export const Route = createFileRoute("/_authenticated/collection")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Your Collection · BidNova" },
      { name: "description", content: "Track the cards you own, their acquisition cost, and estimated collection value." },
    ],
  }),
  component: CollectionPage,
});

const RANGES = ["7d", "30d", "90d", "1y", "all"] as const;

function CollectionPage() {
  const { t } = useTranslation("activity");
  const listFn = useServerFn(listCollections);
  const createFn = useServerFn(createCollection);
  const itemsFn = useServerFn(listCollectionItems);
  const addFn = useServerFn(addCollectionItem);
  const archiveFn = useServerFn(archiveCollectionItem);
  const summaryFn = useServerFn(getCollectionSummary);
  const qc = useQueryClient();

  const [range, setRange] = useState<(typeof RANGES)[number]>("30d");
  const [selected, setSelected] = useState<string | null>(null);

  const cols = useQuery({ queryKey: ["collections"], queryFn: () => listFn() });
  const summary = useQuery({
    queryKey: ["collection-summary", range],
    queryFn: () => summaryFn({ data: { range } }),
  });
  const items = useQuery({
    queryKey: ["collection-items", selected ?? "all"],
    queryFn: () => itemsFn({ data: { collection_id: selected ?? null } }),
  });

  const create = useMutation({
    mutationFn: (name: string) => createFn({ data: { name, is_public: false } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collections"] }),
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 space-y-8">
      <div>
        <p className="text-xs uppercase tracking-widest text-gold">{t("collection.eyebrow", "Collection Manager")}</p>
        <h1 className="mt-1 font-display text-3xl">{t("collection.title", "Your collection")}</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          {t("collection.subtitle", "Track the cards you own separately from what you're selling. Estimated values come from real market data — where market data is unavailable, we show acquisition cost only.")}
        </p>
      </div>

      <SummaryCards summary={summary.data?.summary} loading={summary.isPending} range={range} setRange={setRange} ranges={RANGES} />

      <div className="grid gap-6 md:grid-cols-[240px_1fr]">
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("collection.collectionsHeading", "Collections")}</div>
          <button
            onClick={() => setSelected(null)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm ${selected == null ? "bg-surface-2/60" : "hover:bg-surface-2/40"}`}
          >
            {t("collection.allItems", "All items")}
          </button>
          {(cols.data ?? []).map((c) => (
            <button
              key={c.id}
              onClick={() => setSelected(c.id)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm ${selected === c.id ? "bg-surface-2/60" : "hover:bg-surface-2/40"}`}
            >
              {c.name}
            </button>
          ))}
          <NewCollectionForm onCreate={(name) => create.mutate(name)} pending={create.isPending} />
        </div>

        <div className="space-y-4">
          {selected && (
            <AddItemForm
              collectionId={selected}
              onAdded={() => qc.invalidateQueries({ queryKey: ["collection-items"] })}
              add={(v) => addFn({ data: v })}
            />
          )}
          {items.isPending ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (items.data ?? []).length === 0 ? (
            <div className="rounded-xl border border-border/60 bg-surface-2/40 p-6 text-sm text-muted-foreground text-center">
              {t("collection.noCardsYet", "No cards yet. Create a collection and add your first card.")}
            </div>
          ) : (
            <div className="rounded-xl border border-border/60 bg-surface-2/40 divide-y divide-border/40">
              {(items.data ?? []).map((it) => (
                <div key={it.id} className="flex items-center justify-between p-3 gap-3">
                  <div>
                    <div className="text-sm">{it.card_name ?? t("collection.untitledCard", "Untitled card")}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {[it.game, it.card_set, it.card_number, it.is_graded ? formatGradingBadge({ card_type: "graded", grading_company: it.grading_company, grade: it.grade }) : it.condition]
                        .filter(Boolean)
                        .join(" · ")}
                      · {t("collection.quantityShort", "x{count}", { count: it.quantity })} · {t("collection.acquisitionShort", "acq. {amount}", { amount: formatGBP(it.acquisition_cost_pence ?? 0) })}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      archiveFn({ data: { item_id: it.id, archived: !it.archived } }).then(() =>
                        qc.invalidateQueries({ queryKey: ["collection-items"] }),
                      )
                    }
                  >
                    <Archive className="h-4 w-4" />
                    {it.archived ? t("collection.unarchive", "Unarchive") : t("collection.archive", "Archive")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {summary.data && !summary.data.summary.has_valuations && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-xs">
          {t("collection.noValuationsYet", "No valuations yet. Estimated values will populate once you run price suggestions on your cards.")}
        </div>
      )}
    </div>
  );
}

function SummaryCards({
  summary,
  loading,
  range,
  setRange,
  ranges,
}: {
  summary?: { estimated_value_pence: number; total_acquisition_cost_pence: number; unrealised_gain_pence: number; realised_profit_pence: number; card_count: number };
  loading: boolean;
  range: string;
  setRange: (r: (typeof RANGES)[number]) => void;
  ranges: readonly (typeof RANGES)[number][];
}) {
  const { t } = useTranslation("activity");
  return (
    <div className="space-y-3">
      <div className="flex gap-1 rounded-lg border border-border/60 p-1 w-fit">
        {ranges.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={`px-3 py-1.5 rounded-md text-xs ${range === r ? "bg-gold-gradient text-background" : "text-muted-foreground hover:text-foreground"}`}
          >
            {r}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : summary ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Card label={t("collection.cardsLabel", "Cards")} value={String(summary.card_count)} />
          <Card label={t("collection.acquisitionCostLabel", "Acquisition cost")} value={formatGBP(summary.total_acquisition_cost_pence)} />
          <Card label={t("collection.estimatedValueLabel", "Estimated value")} value={formatGBP(summary.estimated_value_pence)} />
          <Card label={t("collection.realisedProfitLabel", "Realised profit")} value={formatGBP(summary.realised_profit_pence)} />
        </div>
      ) : null}
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-surface-2/40 p-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-2xl">{value}</div>
    </div>
  );
}

function NewCollectionForm({ onCreate, pending }: { onCreate: (name: string) => void; pending: boolean }) {
  const { t } = useTranslation("activity");
  const [name, setName] = useState("");
  return (
    <form
      className="mt-3 flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) {
          onCreate(name.trim());
          setName("");
        }
      }}
    >
      <Input placeholder={t("collection.newCollectionPlaceholder", "New collection")} value={name} onChange={(e) => setName(e.target.value)} />
      <Button type="submit" size="icon" disabled={pending || !name.trim()}>
        <Plus className="h-4 w-4" />
      </Button>
    </form>
  );
}

function AddItemForm({
  collectionId,
  onAdded,
  add,
}: {
  collectionId: string;
  onAdded: () => void;
  add: (v: {
    collection_id: string;
    card_name: string;
    card_set?: string | null;
    quantity: number;
    is_graded: boolean;
    acquisition_cost_pence: number;
  }) => Promise<{ id: string }>;
}) {
  const [name, setName] = useState("");
  const [set, setSet] = useState("");
  const [qty, setQty] = useState("1");
  const [cost, setCost] = useState("");
  const { t } = useTranslation("activity");
  const [graded, setGraded] = useState(false);
  const [pending, setPending] = useState(false);

  return (
    <form
      className="rounded-xl border border-border/60 bg-surface-2/40 p-4 space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setPending(true);
        try {
          await add({
            collection_id: collectionId,
            card_name: name.trim(),
            card_set: set.trim() || null,
            quantity: Math.max(1, parseInt(qty, 10) || 1),
            is_graded: graded,
            acquisition_cost_pence: Math.max(0, Math.round((parseFloat(cost) || 0) * 100)),
          });
          setName("");
          setSet("");
          setQty("1");
          setCost("");
          setGraded(false);
          onAdded();
        } finally {
          setPending(false);
        }
      }}
    >
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("collection.addACard", "Add a card")}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">{t("collection.cardNameLabel", "Card name")}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("collection.setLabel", "Set")}</Label>
          <Input value={set} onChange={(e) => setSet(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("collection.quantityLabel", "Quantity")}</Label>
          <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("collection.acquisitionCostPoundLabel", "Acquisition cost (£)")}</Label>
          <Input type="number" step="0.01" min={0} value={cost} onChange={(e) => setCost(e.target.value)} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={graded} onCheckedChange={setGraded} />
        <span className="text-xs">{t("collection.graded", "Graded")}</span>
      </div>
      <Button type="submit" size="sm" disabled={pending || !name.trim()}>
        {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("collection.addCardButton", "Add card")}
      </Button>
    </form>
  );
}
